/**
 * Breakpoint state for the handful of decisions CSS cannot make.
 *
 * Most responsive behaviour in this app belongs in a Tailwind variant, not
 * here: `hidden lg:block` costs nothing and cannot fall out of step with the
 * stylesheet. This hook is for the cases where the *structure* differs rather
 * than the styling - a table that becomes a list of cards, a sidebar that
 * becomes a drawer with a backdrop and its own open state. Rendering both and
 * hiding one would mean two copies of every row in the DOM, and a drawer that
 * is "open" on a screen where no drawer exists.
 *
 * The widths mirror tailwind.config.js. They are duplicated because a media
 * query string cannot be read out of the compiled stylesheet; if one changes,
 * change both.
 */

import { useEffect, useState } from 'react';

export const BREAKPOINTS = {
    xs: 480,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536
};

/** `useMediaQuery('(min-width: 1024px)')` - true while the query matches. */
export const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(query).matches
    );

    useEffect(() => {
        const list = window.matchMedia(query);
        const onChange = (event) => setMatches(event.matches);

        // Read once on mount as well: the query can already have changed
        // between the initial state above and this effect running.
        setMatches(list.matches);
        list.addEventListener('change', onChange);
        return () => list.removeEventListener('change', onChange);
    }, [query]);

    return matches;
};

/**
 * The three shapes the app is designed for.
 *
 *   mobile  < 768   one column, off-canvas navigation, tables as cards
 *   tablet  768..1023  two-column forms, still an off-canvas nav
 *   desktop >= 1024 the permanent sidebar, and the till's two-pane layout
 *
 * `lg` is the till's dividing line: below it there is not enough width for the
 * item grid and the order panel at once, so the order panel becomes a sheet.
 */
export const useBreakpoint = () => {
    const isTabletUp = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`);
    const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);

    return {
        isMobile: !isTabletUp,
        isTablet: isTabletUp && !isDesktop,
        isDesktop,
        // True wherever the navigation has to be a drawer rather than a column.
        isCompactNav: !isDesktop
    };
};

export default useMediaQuery;
